import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRightLeft, ChefHat, ChevronDown, ChevronRight, Copy, History, Link2, Loader2, Package, Plus, Search, Trash2, Warehouse, Wine, X, Flame, ShieldAlert, Info } from "lucide-react";
import { IngredientPicker } from "@/components/inventory/IngredientPicker";
import { Switch } from "@/components/ui/switch";
import { cachedGet } from "@/lib/refCache";
import { performStockAction } from "@/lib/stockSync";
import { EventType } from "@/lib/events";

/**
 * Session 31.14 — Inventory → Ingredients.
 *
 * Ingredients are the ONLY thing tracked here (menu-item stock counters
 * still work quietly in the background). Every ingredient belongs to the
 * Kitchen or the Bar and has two counts:
 *   • on the floor   (`stock_on_hand`  — recipes deduct from this)
 *   • back of house  (`back_of_house_stock` — freezers / storeroom reserve)
 * The panel is card-based so it reads well on tablets and phones (the old
 * 7-column table squeezed names down to a single letter).
 */

export const UNITS = ["kg", "g", "L", "ml", "pcs", "tots"];
export const CATEGORY_META = {
  kitchen: { label: "Kitchen ingredients", short: "Kitchen", icon: ChefHat },
  bar: { label: "Bar ingredients", short: "Bar", icon: Wine },
};
const EMPTY_DRAFT = { kind: "raw", name: "", category: "kitchen", department_id: "", unit: "pcs", stock_on_hand: "", back_of_house_stock: "", low_stock_threshold: "10", weight_per_unit_g: "", piece_unit: "g", cost_per_unit: "", linked_menu_item_id: "", linked_menu_item_name: "", link_quantity: "1", group: "" };

// Session 63.C — a department maps onto the legacy Kitchen/Bar station bucket
// (bar-routed and NOT kitchen-routed → "bar"; everything else → "kitchen").
export const deptStationCategory = (dept) => (dept && dept.route_to_bar && !dept.route_to_kitchen ? "bar" : "kitchen");

const num = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
const fmtQty = (v) => {
  const n = num(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
};

export function ingredientIsLow(ing) {
  return num(ing.stock_on_hand) <= num(ing.low_stock_threshold, 10);
}

export const IngredientsPanel = ({ http, authHeaders, ingredients, loaded, recorder, onRefresh, onHistory, lowOnly = false, onLowOnlyApplied, sessionForEvents = null }) => {
  const [location, setLocation] = useState("floor"); // floor | back_of_house
  const [filter, setFilter] = useState({ query: "", category: "all", scope: "all", sort: "name_asc" });

  // Low-stock banner → jump straight to the "Low on the floor" view.
  React.useEffect(() => {
    if (!lowOnly) return;
    setLocation("floor");
    setFilter((f) => ({ ...f, scope: "low", category: "all", query: "" }));
    onLowOnlyApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowOnly]);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  // Session 63.C — "Copy ingredient": the source row being duplicated (null =
  // plain add) and the in-dialog picker that chooses it.
  const [copyFrom, setCopyFrom] = useState(null);
  const [copyPickerOpen, setCopyPickerOpen] = useState(false);
  const [copyQuery, setCopyQuery] = useState("");
  // Session 63.C — departments (exactly ONE per ingredient). Only station
  // departments (routed to a kitchen or a bar) can own ingredients.
  const [departments, setDepartments] = useState([]);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await cachedGet(http, "/owner/departments", { headers: authHeaders });
        const list = (r.data?.departments || []).filter((d) => d && d.active !== false && (d.route_to_kitchen || d.route_to_bar));
        if (alive) setDepartments(list);
      } catch (_) { /* best-effort — falls back to Kitchen/Bar buckets */ }
    })();
    return () => { alive = false; };
  }, [http, authHeaders]);
  const deptById = useMemo(() => new Map((departments || []).map((d) => [d.id, d])), [departments]);
  const defaultDeptFor = React.useCallback((cat) => (departments || []).find((d) => deptStationCategory(d) === (cat === "bar" ? "bar" : "kitchen"))?.id || (departments || [])[0]?.id || "", [departments]);
  const deptLabel = (ing) => ing.department_name || deptById.get(ing.department_id)?.name || CATEGORY_META[ing.category === "bar" ? "bar" : "kitchen"].short;
  // Session 66 — STOCK BELONGS TO A DEPARTMENT. The list is sectioned and
  // filtered per department (Kitchen / Bar / Beach Bar / …), not per legacy
  // kitchen-or-bar station bucket, so a second bar's stock is its own list.
  // Ingredients that pre-date departments fall into a legacy station bucket.
  const deptKeyOf = React.useCallback((ing) => (ing.department_id && deptById.has(ing.department_id) ? ing.department_id : `legacy:${ing.category === "bar" ? "bar" : "kitchen"}`), [deptById]);
  const sections = useMemo(() => {
    const order = (d) => (deptStationCategory(d) === "bar" ? 1 : 0);
    const list = [...(departments || [])].sort((a, b) => order(a) - order(b) || String(a.name).localeCompare(String(b.name))).map((d) => {
      const st = deptStationCategory(d);
      return { key: d.id, short: d.name, label: `${d.name} ingredients`, icon: st === "bar" ? Wine : ChefHat, station: st };
    });
    const hasLegacy = { kitchen: false, bar: false };
    (ingredients || []).forEach((ing) => { const k = deptKeyOf(ing); if (k.startsWith("legacy:")) hasLegacy[k.slice(7)] = true; });
    if (hasLegacy.kitchen || !list.length) list.push({ key: "legacy:kitchen", short: list.length ? "Kitchen (unassigned)" : "Kitchen", label: list.length ? "Kitchen ingredients · no department yet" : "Kitchen ingredients", icon: ChefHat, station: "kitchen" });
    if (hasLegacy.bar || !list.length || (!departments?.length)) list.push({ key: "legacy:bar", short: departments?.length ? "Bar (unassigned)" : "Bar", label: departments?.length ? "Bar ingredients · no department yet" : "Bar ingredients", icon: Wine, station: "bar" });
    return list;
  }, [departments, ingredients, deptKeyOf]);
  const startCopy = (ing) => {
    setCopyFrom(ing);
    setCopyPickerOpen(false);
    setCopyQuery("");
    setDraft({
      ...EMPTY_DRAFT,
      kind: "raw",
      name: `${ing.name} (copy)`,
      category: ing.category === "bar" ? "bar" : "kitchen",
      department_id: ing.department_id || "",
      unit: UNITS.includes(ing.unit) ? ing.unit : "pcs",
      low_stock_threshold: String(num(ing.low_stock_threshold, 10)),
      weight_per_unit_g: ing.weight_per_unit_g != null ? String(ing.weight_per_unit_g) : "",
      piece_unit: ing.piece_unit || "g",
      cost_per_unit: ing.cost_per_unit != null ? String(ing.cost_per_unit) : "",
      group: ing.group || "",
    });
    setAddOpen(true);
  };
  const [menuItems, setMenuItems] = useState(null); // lazy-loaded for the "sold as-is" picker
  const [menuQuery, setMenuQuery] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  // Session 41 — grouped, collapsible list + one-at-a-time quick editor.
  const [openGroups, setOpenGroups] = useState(() => { try { return JSON.parse(window.localStorage.getItem("nfcs:ing:open-groups") || "{}"); } catch (_) { return {}; } });
  const [editingId, setEditingId] = useState(null);
  // Session 47 — compact table view (default) vs. the original card view,
  // plus an inline "quick adjust" so stock can be changed without opening
  // the full editor.
  const [dense, setDense] = useState(() => { try { return window.localStorage.getItem("nfcs:ing:dense") !== "0"; } catch (_) { return true; } });
  const toggleDense = () => setDense((d) => { try { window.localStorage.setItem("nfcs:ing:dense", d ? "0" : "1"); } catch (_) { /* noop */ } return !d; });
  const [quick, setQuick] = useState(null); // { id, location: "floor"|"back_of_house", delta: "" , mode: "add"|"set" }
  const [quickSaving, setQuickSaving] = useState(false);
  const applyQuick = async (ing) => {
    if (!quick || quick.id !== ing.id) return;
    const n = Number(quick.value);
    if (!Number.isFinite(n) || (quick.mode === "add" && n === 0)) { toast.error("Enter a quantity."); return; }
    const current = quick.location === "floor" ? num(ing.stock_on_hand) : num(ing.back_of_house_stock);
    const body = quick.mode === "add"
      ? { ingredient_id: ing.id, location: quick.location, mode: n > 0 ? "add" : "remove", quantity: Math.abs(n), expected: current, reason: "manual_edit", user: recorder || null }
      : { ingredient_id: ing.id, location: quick.location, mode: "set", quantity: n, expected: current, reason: "stock_count", user: recorder || null };
    setQuickSaving(true);
    try {
      const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/adjust", body, eventType: EventType.STOCK_ADJUSTED, session: sessionForEvents || null });
      toast.success(`${ing.name}: ${quick.location === "floor" ? "floor" : "back of house"} ${quick.mode === "add" ? (n > 0 ? `+${fmtQty(n)}` : fmtQty(n)) : `set to ${fmtQty(n)}`}${res.status === "queued" ? " (will sync)" : ""}`);
      setQuick(null);
      if (res.status !== "queued") await onRefresh?.();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Update failed.");
    } finally {
      setQuickSaving(false);
    }
  };
  const expandAll = (open) => {
    const next = {};
    Object.keys(grouped || {}).forEach((key) => (grouped[key] || []).forEach(([g]) => { next[`${key}:${g}`] = open; }));
    setOpenGroups(next);
    try { window.localStorage.setItem("nfcs:ing:open-groups", JSON.stringify(next)); } catch (_) { /* noop */ }
  };
  const [effective, setEffective] = useState(null); // id → effective status (policy × visibility × stock)
  const [locationsMeta, setLocationsMeta] = useState([]);
  const [cfg, setCfg] = useState({}); // id → config edits (group / policy / prep)
  const [cfgSaving, setCfgSaving] = useState(false);
  const toggleGroup = (key) => setOpenGroups((g) => { const n = { ...g, [key]: !g[key] }; try { window.localStorage.setItem("nfcs:ing:open-groups", JSON.stringify(n)); } catch (_) { /* noop */ } return n; });
  const loadEffective = React.useCallback(async () => {
    try {
      const [a, b] = await Promise.all([cachedGet(http, "/owner/ingredients/effective-status", { headers: authHeaders }), cachedGet(http, "/owner/stock-locations", { headers: authHeaders })]);
      setEffective(a.data || null);
      setLocationsMeta((b.data?.locations || []).filter((l) => l.active !== false));
    } catch (_) { /* best-effort */ }
  }, [http, authHeaders]);
  React.useEffect(() => { loadEffective(); }, [loadEffective, ingredients]);

  const loadMenuItems = async () => {
    if (menuItems) return;
    try {
      const r = await http.get("/owner/menu-items", { headers: authHeaders });
      const list = Array.isArray(r.data) ? r.data : (r.data?.menu_items || r.data?.items || []);
      setMenuItems(list);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load menu items.");
      setMenuItems([]);
    }
  };
  const linkedItemIds = useMemo(() => new Set((ingredients || []).map((i) => i.linked_menu_item_id).filter(Boolean)), [ingredients]);

  const pickMenuItem = (mi) => {
    const dept = String(mi.department || "").toLowerCase();
    // Session 63.C — the menu item's department IS the ingredient's department.
    const match = (departments || []).find((d) => String(d.name || "").toLowerCase() === dept);
    setDraft((d) => ({
      ...d,
      linked_menu_item_id: mi.id,
      linked_menu_item_name: mi.name,
      name: d.name && d.linked_menu_item_id ? d.name : mi.name,
      unit: "pcs",
      category: match ? deptStationCategory(match) : (dept === "bar" ? "bar" : (dept === "kitchen" ? "kitchen" : d.category)),
      department_id: match ? match.id : d.department_id,
      stock_on_hand: mi.stock !== null && mi.stock !== undefined ? String(mi.stock) : d.stock_on_hand,
      cost_per_unit: mi.cost !== null && mi.cost !== undefined ? String(mi.cost) : d.cost_per_unit,
    }));
  };
  const [moveDirection, setMoveDirection] = useState("to_floor");

  const isBoh = location === "back_of_house";
  const stockField = isBoh ? "back_of_house_stock" : "stock_on_hand";

  const counts = useMemo(() => {
    const out = { kitchen: 0, bar: 0, low: 0, bohTotal: 0, byKey: {} };
    (ingredients || []).forEach((ing) => {
      out[ing.category === "bar" ? "bar" : "kitchen"] += 1;
      const k = deptKeyOf(ing);
      out.byKey[k] = (out.byKey[k] || 0) + 1;
      if (ingredientIsLow(ing)) out.low += 1;
      if (num(ing.back_of_house_stock) > 0) out.bohTotal += 1;
    });
    return out;
  }, [ingredients, deptKeyOf]);

  const visible = useMemo(() => {
    const q = (filter.query || "").trim().toLowerCase();
    let list = (ingredients || []).filter((ing) => {
      if (q && !(ing.name || "").toLowerCase().includes(q)) return false;
      if (filter.category !== "all" && deptKeyOf(ing) !== filter.category) return false;
      const stock = num(ing[stockField]);
      if (filter.scope === "low" && !ingredientIsLow(ing)) return false;
      if (filter.scope === "zero" && stock > 0) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      switch (filter.sort) {
        case "name_desc": return bn.localeCompare(an);
        case "stock_asc": return num(a[stockField]) - num(b[stockField]) || an.localeCompare(bn);
        case "stock_desc": return num(b[stockField]) - num(a[stockField]) || an.localeCompare(bn);
        default: return an.localeCompare(bn);
      }
    });
    return list;
  }, [ingredients, filter, stockField, deptKeyOf]);

  const groups = useMemo(() => {
    const g = { kitchen: [], bar: [] };
    visible.forEach((ing) => g[ing.category === "bar" ? "bar" : "kitchen"].push(ing));
    return g;
  }, [visible]);

  const setEdit = (id, patch) => setEdits((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));
  const clearEdit = (id) => setEdits((s) => { const n = { ...s }; delete n[id]; return n; });

  const save = async (ing) => {
    const e = edits[ing.id];
    if (!e || Object.keys(e).length === 0) return;
    const patch = {};
    if (e.name !== undefined) patch.name = e.name;
    if (e.unit !== undefined) patch.unit = e.unit;
    if (e.category !== undefined) patch.category = e.category;
    if (e.department_id !== undefined && e.department_id) patch.department_id = e.department_id;
    if (e.stock_on_hand !== undefined && e.stock_on_hand !== "") patch.stock_on_hand = Number(e.stock_on_hand);
    if (e.back_of_house_stock !== undefined && e.back_of_house_stock !== "") patch.back_of_house_stock = Number(e.back_of_house_stock);
    if (e.low_stock_threshold !== undefined && e.low_stock_threshold !== "") patch.low_stock_threshold = Number(e.low_stock_threshold);
    if (e.weight_per_unit_g !== undefined) {
      if (e.weight_per_unit_g === "") patch.clear_weight_per_unit_g = true;
      else patch.weight_per_unit_g = Number(e.weight_per_unit_g);
    }
    if (e.piece_unit !== undefined) patch.piece_unit = e.piece_unit;
    if (e.cost_per_unit !== undefined && e.cost_per_unit !== "") patch.cost_per_unit = Number(e.cost_per_unit);
    // Session 40 — balance changes are stock EVENTS (offline-safe, applied
    // once, absolute set guarded by the balance the list showed). Config
    // fields (name / unit / cost …) still go through the plain PATCH.
    const stockSets = [];
    if (patch.stock_on_hand !== undefined) { stockSets.push({ location: "floor", quantity: patch.stock_on_hand, expected: num(ing.stock_on_hand) }); delete patch.stock_on_hand; }
    if (patch.back_of_house_stock !== undefined) { stockSets.push({ location: "back_of_house", quantity: patch.back_of_house_stock, expected: num(ing.back_of_house_stock) }); delete patch.back_of_house_stock; }
    setSavingId(ing.id);
    let queued = false;
    try {
      if (Object.keys(patch).length) {
        await http.patch(`/owner/ingredients/${ing.id}`, patch, { headers: authHeaders });
      }
      for (const st of stockSets) {
        const body = { ingredient_id: ing.id, location: st.location, mode: "set", quantity: st.quantity, expected: st.expected, reason: "manual_edit", user: recorder || null };
        const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/adjust", body, eventType: EventType.STOCK_ADJUSTED, session: sessionForEvents || null });
        if (res.status === "queued") queued = true;
      }
      if (queued) toast.info(`${patch.name || ing.name}: stock change saved on this device — it will sync when the connection returns.`);
      else toast.success(`${patch.name || ing.name} updated.`);
      clearEdit(ing.id);
      if (!queued) await onRefresh?.();
    } catch (error) {
      const d = error.response?.data?.detail;
      if (d && d.code === "balance_moved") toast.error(`${ing.name}: stock at ${d.location_name} is now ${d.server_balance}, not ${d.device_expected} as shown. Refresh and try again.`);
      else toast.error((typeof d === "string" ? d : d?.message) || "Update failed.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (ing) => {
    if (!window.confirm(`Delete ingredient "${ing.name}"?`)) return;
    try {
      await http.delete(`/owner/ingredients/${ing.id}`, { headers: authHeaders });
      toast.success("Ingredient removed.");
      await onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed.");
    }
  };

  const create = async () => {
    const name = (draft.name || "").trim();
    if (!name) { toast.error("Give the ingredient a name."); return; }
    if (draft.kind === "item" && !draft.linked_menu_item_id) { toast.error("Pick the menu item this stock is sold as."); return; }
    if ((departments || []).length && !draft.department_id) { toast.error("Pick the department this ingredient is used by."); return; }
    const deptDoc = draft.department_id ? deptById.get(draft.department_id) : null;
    const category = deptDoc ? deptStationCategory(deptDoc) : (draft.category || "kitchen");
    const deptName = deptDoc?.name || CATEGORY_META[category].short;
    const body = {
      name,
      category,
      unit: draft.unit || "pcs",
      stock_on_hand: num(draft.stock_on_hand),
      back_of_house_stock: num(draft.back_of_house_stock),
      low_stock_threshold: num(draft.low_stock_threshold, 10),
    };
    if (draft.department_id) body.department_id = draft.department_id;
    if (draft.unit === "pcs" && draft.weight_per_unit_g !== "") {
      body.weight_per_unit_g = Number(draft.weight_per_unit_g);
      body.piece_unit = draft.piece_unit || "g";
    }
    if (draft.cost_per_unit !== "" && Number.isFinite(Number(draft.cost_per_unit))) body.cost_per_unit = Number(draft.cost_per_unit);
    if ((draft.group || "").trim()) body.group = draft.group.trim();
    if (draft.kind === "item" && draft.linked_menu_item_id) {
      body.linked_menu_item_id = draft.linked_menu_item_id;
      body.link_quantity = Math.max(Number(draft.link_quantity) || 1, 0.001);
    }
    setAdding(true);
    try {
      if (copyFrom) {
        // Session 63.C — server-side copy keeps prep recipe / fallback policy /
        // location priorities from the source; the fields on this form override.
        await http.post(`/owner/ingredients/${copyFrom.id}/copy`, {
          name,
          department_id: draft.department_id || undefined,
          copy_stock: false,
          unit: body.unit,
          low_stock_threshold: body.low_stock_threshold,
          cost_per_unit: body.cost_per_unit,
          stock_on_hand: body.stock_on_hand,
          back_of_house_stock: body.back_of_house_stock,
          group: (draft.group || "").trim() || undefined,
        }, { headers: authHeaders });
        toast.success(`${name} added to ${deptName} — copied from ${copyFrom.name}.`);
      } else {
        await http.post("/owner/ingredients", body, { headers: authHeaders });
        toast.success(draft.kind === "item"
          ? `${name} added — every sale of "${draft.linked_menu_item_name}" now deducts ${body.link_quantity} ${body.unit}.`
          : `${name} added to ${deptName}.`);
      }
      setDraft({ ...EMPTY_DRAFT, category: draft.category, department_id: draft.department_id });
      setCopyFrom(null);
      setAddOpen(false);
      await onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add ingredient.");
    } finally {
      setAdding(false);
    }
  };

  // Session 63.C — "Move stock" moved to Inventory → Stock Take (App.js);
  // the dialog is still exported from this file for that screen.
  const openMove = (direction) => { setMoveDirection(direction); setMoveOpen(true); };
  void openMove;

  // ── Session 41 — grouping ────────────────────────────────────────────
  const OTHER_GROUP = "Other";
  const groupOf = (ing) => (ing.group || "").trim() || OTHER_GROUP;
  const searching = (filter.query || "").trim().length > 0 || filter.scope !== "all";
  const groupSuggestions = useMemo(() => {
    const set = new Set(["Dairy", "Meat", "Produce", "Sauces", "Dry goods", "Frozen", "Bakery", "Spirits", "Beer", "Wine", "Soft drinks", "Mixers", "Garnish", "Prepared"]);
    (ingredients || []).forEach((i) => { if (i.group) set.add(i.group); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [ingredients]);
  const grouped = useMemo(() => {
    const out = {};
    sections.forEach((s) => { out[s.key] = new Map(); });
    visible.forEach((ing) => {
      const key = deptKeyOf(ing);
      if (!out[key]) out[key] = new Map();
      const g = groupOf(ing);
      if (!out[key].has(g)) out[key].set(g, []);
      out[key].get(g).push(ing);
    });
    const sortGroups = (m) => [...m.entries()].sort((a, b) => (a[0] === OTHER_GROUP ? 1 : b[0] === OTHER_GROUP ? -1 : a[0].localeCompare(b[0])));
    const res = {};
    Object.keys(out).forEach((k) => { res[k] = sortGroups(out[k]); });
    return res;
  }, [visible, sections, deptKeyOf]);

  const cfgOf = (ing) => ({
    group: ing.group || "", fallback_policy: ing.fallback_policy || "default", is_prep_item: !!ing.is_prep_item, prep_station: ing.prep_station || (ing.category === "bar" ? "bar" : "kitchen"),
    prep_recipe: (ing.prep_recipe || []).map((l) => ({ ...l })), prep_source_location_id: ing.prep_source_location_id || "", prep_output_location_id: ing.prep_output_location_id || "",
    ...(cfg[ing.id] || {}),
  });
  const setCfgFor = (id, patch) => setCfg((c) => ({ ...c, [id]: { ...(c[id] || {}), ...patch } }));
  const saveCfg = async (ing) => {
    const c = cfgOf(ing);
    const body = { is_prep_item: !!c.is_prep_item, prep_station: c.prep_station || null };
    if ((c.group || "").trim()) body.group = c.group.trim(); else body.clear_group = true;
    if (c.fallback_policy && c.fallback_policy !== "default") body.fallback_policy = c.fallback_policy; else body.clear_fallback_policy = true;
    if (c.is_prep_item) {
      body.prep_recipe = (c.prep_recipe || []).filter((l) => l.ingredient_id && Number(l.quantity) > 0).map((l) => ({ ingredient_id: l.ingredient_id, quantity: Number(l.quantity), unit: l.unit || undefined }));
      body.prep_source_location_id = c.prep_source_location_id || "";
      body.prep_output_location_id = c.prep_output_location_id || "";
    }
    setCfgSaving(true);
    try {
      await http.patch(`/owner/ingredients/${ing.id}`, body, { headers: authHeaders });
      toast.success(`${ing.name}: settings saved.`);
      setCfg((x) => { const n = { ...x }; delete n[ing.id]; return n; });
      await onRefresh?.();
      loadEffective();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not save settings.");
    } finally {
      setCfgSaving(false);
    }
  };

  const POLICY_META = {
    default: { label: "Business default", hint: "follows the Automatic stock fallback setting" },
    auto: { label: "Service stock first, then fallback", hint: "uses BOH / other locations automatically when service stock runs out" },
    none: { label: "Do not fallback", hint: "OUT OF STOCK AT SERVICE LOCATION — never takes BOH stock automatically" },
    manager: { label: "Require manager approval", hint: "staff must get a manager to allow BOH stock" },
  };

  // ── Row (compact) ────────────────────────────────────────────────────
  const renderQuick = (ing) => {
    if (!quick || quick.id !== ing.id) return null;
    const unit = UNITS.includes(ing.unit) ? ing.unit : "pcs";
    return (
      <div className="flex flex-wrap items-center gap-2 border-t border-border bg-secondary/40 px-3 py-2" data-testid={`ingredient-quick-${ing.id}`} onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
          {[{ id: "floor", l: "Floor" }, { id: "back_of_house", l: "Back of house" }].map((o) => (
            <button key={o.id} type="button" onClick={() => setQuick((q) => ({ ...q, location: o.id }))} className={`h-8 rounded-md px-2 text-xs font-medium ${quick.location === o.id ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid={`ingredient-quick-loc-${o.id}-${ing.id}`}>{o.l}</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
          {[{ id: "add", l: "+ / − amount" }, { id: "set", l: "Set count" }].map((o) => (
            <button key={o.id} type="button" onClick={() => setQuick((q) => ({ ...q, mode: o.id }))} className={`h-8 rounded-md px-2 text-xs font-medium ${quick.mode === o.id ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid={`ingredient-quick-mode-${o.id}-${ing.id}`}>{o.l}</button>
          ))}
        </div>
        <Input autoFocus type="number" inputMode="decimal" step="any" value={quick.value} onChange={(e) => setQuick((q) => ({ ...q, value: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") applyQuick(ing); if (e.key === "Escape") setQuick(null); }} placeholder={quick.mode === "add" ? `e.g. 12 or -3 ${unit}` : `new ${unit} count`} className="h-9 w-40 rounded-lg tabular-nums" data-testid={`ingredient-quick-value-${ing.id}`} />
        <span className="text-xs text-muted-foreground">now {fmtQty(quick.location === "floor" ? num(ing.stock_on_hand) : num(ing.back_of_house_stock))} {unit}</span>
        <Button size="sm" className="h-9 rounded-lg" onClick={() => applyQuick(ing)} disabled={quickSaving} data-testid={`ingredient-quick-save-${ing.id}`}>{quickSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
        <Button size="sm" variant="ghost" className="h-9 rounded-lg" onClick={() => setQuick(null)} data-testid={`ingredient-quick-cancel-${ing.id}`}>Cancel</Button>
      </div>
    );
  };

  const renderDenseRow = (ing) => {
    const floor = num(ing.stock_on_hand);
    const boh = num(ing.back_of_house_stock);
    const low = ingredientIsLow(ing);
    const editing = editingId === ing.id;
    const unit = UNITS.includes(ing.unit) ? ing.unit : "pcs";
    return (
      <div key={ing.id} className={`border-b border-border last:border-b-0 ${editing ? "bg-[hsl(var(--primary))]/5" : "hover:bg-secondary/40"}`} data-testid={`ingredient-row-${ing.id}`}>
        <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2 py-1 md:grid-cols-[minmax(0,1fr)_84px_84px_90px_auto]">
          <button type="button" onClick={() => setEditingId(editing ? null : ing.id)} className="flex min-w-0 items-center gap-1.5 text-left" aria-expanded={editing} data-testid={`ingredient-open-${ing.id}`}>
            {editing ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
            <span className="truncate text-sm font-medium" data-testid={`ingredient-row-name-${ing.id}`}>{ing.name}</span>
            <span className="hidden shrink-0 rounded-md bg-secondary px-1.5 text-[10px] font-medium text-muted-foreground sm:inline" data-testid={`ingredient-row-dept-${ing.id}`}>{deptLabel(ing)}</span>
            {ing.is_prep_item ? <Flame className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent))]" title="Prepared in-house" /> : null}
            {ing.linked_menu_item_id ? <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" title="Sold as-is" /> : null}
            {low ? <Badge className="ml-1 shrink-0 rounded-md bg-destructive/10 px-1.5 text-[10px] text-destructive hover:bg-destructive/10" data-testid={`ingredient-low-${ing.id}`}>Low</Badge> : null}
          </button>
          <span className={`hidden text-right text-sm tabular-nums md:block ${low ? "font-semibold text-destructive" : ""}`} title="On the floor" data-testid={`ingredient-row-floor-${ing.id}`}>{fmtQty(floor)} <span className="text-[10px] text-muted-foreground">{unit}</span></span>
          <span className="hidden text-right text-sm tabular-nums md:block" title="Back of house" data-testid={`ingredient-row-boh-${ing.id}`}>{fmtQty(boh)} <span className="text-[10px] text-muted-foreground">{unit}</span></span>
          <span className="hidden text-right text-xs tabular-nums text-muted-foreground md:block">{ing.cost_per_unit != null ? `R${fmtQty(ing.cost_per_unit)}/${unit}` : "—"}</span>
          <div className="flex items-center justify-end gap-1">
            <span className="text-xs tabular-nums text-muted-foreground md:hidden" data-testid={`ingredient-row-balances-${ing.id}`}><span className={low ? "font-semibold text-destructive" : "text-foreground"}>{fmtQty(floor)}</span>/{fmtQty(boh)} {unit}</span>
            <Button type="button" size="sm" variant={quick?.id === ing.id ? "secondary" : "ghost"} className="h-8 rounded-lg px-2 text-xs" onClick={(e) => { e.stopPropagation(); setQuick(quick?.id === ing.id ? null : { id: ing.id, location: "floor", mode: "add", value: "" }); }} data-testid={`ingredient-quick-open-${ing.id}`}>± Stock</Button>
          </div>
        </div>
        {renderQuick(ing)}
        {editing ? renderEditor(ing) : null}
      </div>
    );
  };

  const renderRow = (ing) => {
    if (dense) return renderDenseRow(ing);
    const floor = num(ing.stock_on_hand);
    const boh = num(ing.back_of_house_stock);
    const low = ingredientIsLow(ing);
    const eff = effective?.status?.[ing.id];
    const editing = editingId === ing.id;
    const unit = UNITS.includes(ing.unit) ? ing.unit : "pcs";
    return (
      <div key={ing.id} className={`rounded-2xl border bg-card transition-colors ${editing ? "border-[hsl(var(--primary))]/60 ring-2 ring-[hsl(var(--primary))]/20" : "border-border hover:bg-secondary/40"}`} data-testid={`ingredient-row-${ing.id}`}>
        <button
          type="button"
          onClick={() => setEditingId(editing ? null : ing.id)}
          className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left"
          aria-expanded={editing}
          data-testid={`ingredient-open-${ing.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="truncate text-base font-semibold" data-testid={`ingredient-row-name-${ing.id}`}>{ing.name}</span>
              <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`ingredient-row-dept-${ing.id}`}>{deptLabel(ing)}</Badge>
              {ing.is_prep_item ? <Badge variant="outline" className="rounded-full border-[hsl(var(--accent))]/50 text-[10px]" data-testid={`ingredient-prep-badge-${ing.id}`}><Flame className="mr-1 h-3 w-3" />Prepared in-house</Badge> : null}
              {ing.linked_menu_item_id ? <Badge variant="outline" className="rounded-full text-[10px]"><Link2 className="mr-1 h-3 w-3" />Sold as-is</Badge> : null}
              {eff && eff.override ? <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`ingredient-policy-badge-${ing.id}`}><ShieldAlert className="mr-1 h-3 w-3" />{eff.policy_label}</Badge> : null}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`ingredient-row-balances-${ing.id}`}>
              <span className={low ? "font-semibold text-destructive" : "font-medium text-foreground"}>{fmtQty(floor)} {unit}</span> on the floor · <span className="font-medium text-foreground">{fmtQty(boh)} {unit}</span> back of house
              {ing.cost_per_unit != null ? <> · R{fmtQty(ing.cost_per_unit)}/{unit}</> : null}
            </p>
          </div>
          {low ? <Badge className="shrink-0 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/10" data-testid={`ingredient-low-${ing.id}`}>Low</Badge> : null}
          {editing ? <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />}
        </button>
        {editing ? renderEditor(ing) : null}
      </div>
    );
  };

  // ── Quick editor (expanded row) ──────────────────────────────────────
  const renderEditor = (ing) => {
    const e = edits[ing.id] || {};
    const hasEdits = Object.keys(e).length > 0;
    const unit = e.unit ?? (UNITS.includes(ing.unit) ? ing.unit : "pcs");
    const floor = num(ing.stock_on_hand);
    const boh = num(ing.back_of_house_stock);
    const threshold = num(ing.low_stock_threshold, 10);
    const eff = effective?.status?.[ing.id];
    const c = cfgOf(ing);
    const cfgDirty = !!cfg[ing.id];
    const rawCandidates = (ingredients || []).filter((i) => i.id !== ing.id && !i.is_prep_item);
    return (
      <div className="space-y-4 border-t border-border p-3 sm:p-4" data-testid={`ingredient-editor-${ing.id}`}>
        {/* Stock + basics */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Name</Label>
            <Input value={e.name ?? ing.name ?? ""} onChange={(ev) => setEdit(ing.id, { name: ev.target.value })} className="mt-1 h-11 rounded-xl text-base font-medium" data-testid={`ingredient-name-${ing.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">On the floor ({unit})</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={e.stock_on_hand ?? floor} onChange={(ev) => setEdit(ing.id, { stock_on_hand: ev.target.value })} className="mt-1 h-11 rounded-xl text-base font-semibold tabular-nums" data-testid={`ingredient-stock-${ing.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Back of house ({unit})</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={e.back_of_house_stock ?? boh} onChange={(ev) => setEdit(ing.id, { back_of_house_stock: ev.target.value })} className="mt-1 h-11 rounded-xl text-base font-semibold tabular-nums" data-testid={`ingredient-boh-stock-${ing.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Low alert ≤</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={e.low_stock_threshold ?? threshold} onChange={(ev) => setEdit(ing.id, { low_stock_threshold: ev.target.value })} className="mt-1 h-11 rounded-xl tabular-nums" data-testid={`ingredient-threshold-${ing.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Cost / {unit} (R)</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={e.cost_per_unit ?? (ing.cost_per_unit ?? "")} onChange={(ev) => setEdit(ing.id, { cost_per_unit: ev.target.value })} placeholder="—" className="mt-1 h-11 rounded-xl tabular-nums" data-testid={`ingredient-cost-${ing.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Unit</Label>
            <Select value={unit} onValueChange={(v) => setEdit(ing.id, { unit: v })}>
              <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-unit-${ing.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Used by</Label>
            {(departments || []).length ? (
              <Select value={e.department_id ?? (ing.department_id || defaultDeptFor(ing.category) || "")} onValueChange={(v) => setEdit(ing.id, { department_id: v })}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-department-${ing.id}`}><SelectValue placeholder="Pick a department" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Select value={e.category ?? (ing.category || "kitchen")} onValueChange={(v) => setEdit(ing.id, { category: v })}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-category-${ing.id}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="kitchen">Kitchen</SelectItem><SelectItem value="bar">Bar</SelectItem></SelectContent>
              </Select>
            )}
          </div>
          {unit === "pcs" ? (
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Each piece equals</Label>
              <div className="mt-1 flex items-center gap-1">
                <Input type="number" min="0" step="0.1" inputMode="decimal" value={e.weight_per_unit_g ?? (ing.weight_per_unit_g ?? "")} onChange={(ev) => setEdit(ing.id, { weight_per_unit_g: ev.target.value })} placeholder="optional" className="h-11 rounded-xl tabular-nums" data-testid={`ingredient-weight-${ing.id}`} />
                <Select value={e.piece_unit ?? (ing.piece_unit || "g")} onValueChange={(v) => setEdit(ing.id, { piece_unit: v })}>
                  <SelectTrigger className="h-11 w-20 shrink-0 rounded-xl" data-testid={`ingredient-piece-unit-${ing.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="g">g</SelectItem><SelectItem value="ml">ml</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!hasEdits || savingId === ing.id} onClick={() => save(ing)} className="h-10 rounded-xl bg-[hsl(var(--accent))] px-4 text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid={`ingredient-save-${ing.id}`}>
            {savingId === ing.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
          {hasEdits ? <Button size="sm" variant="ghost" className="h-10 rounded-xl" onClick={() => clearEdit(ing.id)} data-testid={`ingredient-undo-${ing.id}`}>Undo</Button> : null}
          <Button size="sm" variant="outline" className="h-10 rounded-xl" onClick={() => onHistory?.(ing.id, ing.name)} data-testid={`ingredient-history-${ing.id}`}><History className="h-4 w-4" /> History</Button>
          <Button size="sm" variant="outline" className="h-10 rounded-xl" onClick={() => startCopy(ing)} title="Duplicate this ingredient (e.g. for another department)" data-testid={`ingredient-copy-${ing.id}`}><Copy className="h-4 w-4" /> Copy</Button>
          <Button size="sm" variant="ghost" onClick={() => remove(ing)} className="ml-auto h-10 w-10 rounded-xl p-0 text-destructive hover:bg-destructive/10" aria-label={`Delete ${ing.name}`} data-testid={`ingredient-delete-${ing.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>

        {/* Session 41 — Group · Fallback policy · Prepared in-house */}
        <div className="grid gap-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-3 lg:grid-cols-2" data-testid={`ingredient-settings-${ing.id}`}>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Group</Label>
            <Input list={`ing-groups-${ing.id}`} value={c.group} onChange={(ev) => setCfgFor(ing.id, { group: ev.target.value })} placeholder="e.g. Dairy, Meat, Produce…" className="mt-1 h-11 rounded-xl bg-card" data-testid={`ingredient-group-${ing.id}`} />
            <datalist id={`ing-groups-${ing.id}`}>{groupSuggestions.map((g) => <option key={g} value={g} />)}</datalist>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {groupSuggestions.slice(0, 8).map((g) => (
                <button key={g} type="button" onClick={() => setCfgFor(ing.id, { group: g })} className={`h-7 rounded-full border px-2.5 text-[11px] ${c.group === g ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 font-semibold" : "border-border bg-card text-muted-foreground hover:text-foreground"}`} data-testid={`ingredient-group-chip-${ing.id}-${g.replace(/\s+/g, "-").toLowerCase()}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">When service stock runs out</Label>
            <Select value={c.fallback_policy} onValueChange={(v) => setCfgFor(ing.id, { fallback_policy: v })}>
              <SelectTrigger className="mt-1 h-11 rounded-xl bg-card" data-testid={`ingredient-policy-${ing.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(POLICY_META).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`ingredient-policy-option-${k}`}><span className="font-medium">{m.label}</span><span className="ml-1 text-xs text-muted-foreground">— {m.hint}</span></SelectItem>)}
              </SelectContent>
            </Select>
            {eff ? (
              <div className="mt-2 flex items-start gap-2 rounded-xl border border-border bg-card p-2.5 text-xs" data-testid={`ingredient-effective-${ing.id}`}>
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--primary))]" />
                <div>
                  <p className="font-semibold">Effective result</p>
                  <p className="text-muted-foreground">{eff.effective}</p>
                  <p className="mt-1 text-muted-foreground">Service stock ({eff.preferred_location_name}): <b className="text-foreground">{fmtQty(eff.preferred_balance)}</b> · Fallback stock available: <b className="text-foreground">{fmtQty(eff.fallback_available)}</b>
                    {eff.used_by_count ? <> · Used by {eff.used_by_count} menu item{eff.used_by_count === 1 ? "" : "s"}{eff.menu_hidden_when_oos === true ? " (hidden when out of stock)" : eff.menu_hidden_when_oos === false ? " (stay visible)" : ""}</> : null}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
          <div className="lg:col-span-2">
            <label className="flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card px-3" data-testid={`ingredient-prep-toggle-${ing.id}`}>
              <span>
                <span className="flex items-center gap-1.5 text-sm font-semibold"><Flame className="h-4 w-4 text-[hsl(var(--accent))]" /> Prepared in-house (prep item)</span>
                <span className="block text-[11px] text-muted-foreground">Appears on Prep Sheets. Confirming production consumes the inputs below and adds the prepared stock to the output location.</span>
              </span>
              <Switch checked={c.is_prep_item} onCheckedChange={(v) => setCfgFor(ing.id, { is_prep_item: v })} data-testid={`ingredient-prep-switch-${ing.id}`} />
            </label>
            {c.is_prep_item ? (
              <div className="mt-3 space-y-3 rounded-xl border border-border bg-card p-3" data-testid={`ingredient-prep-config-${ing.id}`}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Prepared at</Label>
                    <Select value={c.prep_station} onValueChange={(v) => setCfgFor(ing.id, { prep_station: v })}>
                      <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-prep-station-${ing.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="kitchen">Kitchen</SelectItem><SelectItem value="bar">Bar</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Inputs taken from</Label>
                    <Select value={c.prep_source_location_id || "__default"} onValueChange={(v) => setCfgFor(ing.id, { prep_source_location_id: v === "__default" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-prep-source-${ing.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="__default">Back of house (default)</SelectItem>{locationsMeta.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Prepared stock goes to</Label>
                    <Select value={c.prep_output_location_id || "__default"} onValueChange={(v) => setCfgFor(ing.id, { prep_output_location_id: v === "__default" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`ingredient-prep-output-${ing.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="__default">Floor / service (default)</SelectItem>{locationsMeta.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Inputs per 1 {unit} of {ing.name}</p>
                  <div className="mt-1 space-y-2">
                    {(c.prep_recipe || []).map((l, idx) => {
                      const raw = rawCandidates.find((r) => r.id === l.ingredient_id);
                      return (
                        <div key={l._k || `${l.ingredient_id || "line"}-${idx}`} className="flex flex-wrap items-center gap-2" data-testid={`ingredient-prep-line-${ing.id}-${idx}`}>
                          <div className="min-w-[180px] flex-1">
                            <IngredientPicker ingredients={rawCandidates} value={l.ingredient_id} onChange={(id) => { const r = rawCandidates.find((x) => x.id === id); setCfgFor(ing.id, { prep_recipe: c.prep_recipe.map((x, i) => i === idx ? { ...x, ingredient_id: id, unit: r?.unit || x.unit } : x) }); }} testId={`ingredient-prep-line-ing-${ing.id}-${idx}`} />
                          </div>
                          <Input type="number" min="0" step="0.01" inputMode="decimal" value={l.quantity ?? ""} onChange={(ev) => setCfgFor(ing.id, { prep_recipe: c.prep_recipe.map((x, i) => i === idx ? { ...x, quantity: ev.target.value } : x) })} className="h-11 w-24 rounded-xl tabular-nums" placeholder="qty" data-testid={`ingredient-prep-line-qty-${ing.id}-${idx}`} />
                          <Select value={l.unit || raw?.unit || "pcs"} onValueChange={(v) => setCfgFor(ing.id, { prep_recipe: c.prep_recipe.map((x, i) => i === idx ? { ...x, unit: v } : x) })}>
                            <SelectTrigger className="h-11 w-20 rounded-xl" data-testid={`ingredient-prep-line-unit-${ing.id}-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button type="button" size="icon" variant="ghost" className="h-10 w-10 rounded-xl text-destructive" onClick={() => setCfgFor(ing.id, { prep_recipe: c.prep_recipe.filter((_, i) => i !== idx) })} data-testid={`ingredient-prep-line-remove-${ing.id}-${idx}`}><X className="h-4 w-4" /></Button>
                        </div>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm" className="h-10 rounded-xl" onClick={() => setCfgFor(ing.id, { prep_recipe: [...(c.prep_recipe || []), { _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, ingredient_id: "", quantity: "", unit: "" }] })} data-testid={`ingredient-prep-add-input-${ing.id}`}><Plus className="h-4 w-4" /> Add input</Button>
                    {!(c.prep_recipe || []).length ? <p className="text-[11px] text-muted-foreground">No inputs = a checklist task on the prep sheet (no stock effect).</p> : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2 lg:col-span-2">
            <Button size="sm" disabled={!cfgDirty || cfgSaving} onClick={() => saveCfg(ing)} className="h-10 rounded-xl" data-testid={`ingredient-settings-save-${ing.id}`}>{cfgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings</Button>
            {cfgDirty ? <Button size="sm" variant="ghost" className="h-10 rounded-xl" onClick={() => setCfg((x) => { const n = { ...x }; delete n[ing.id]; return n; })} data-testid={`ingredient-settings-undo-${ing.id}`}>Undo</Button> : null}
          </div>
        </div>
      </div>
    );
  };

  const renderCategory = (section) => {
    const key = section.key;
    const meta = section;
    const groupsFor = grouped[key] || [];
    if (filter.category !== "all" && filter.category !== key) return null;
    const Icon = meta.icon;
    const total = groupsFor.reduce((n, [, list]) => n + list.length, 0);
    // In the "All" view, hide departments that have nothing yet (but always
    // show the one the owner has tapped).
    if (filter.category === "all" && total === 0 && !searching && (departments || []).length > 1) return null;
    const testKey = key.startsWith("legacy:") ? key.slice(7) : `dept-${key}`;
    return (
      <section key={key} className="space-y-2" data-testid={`ingredients-group-${testKey}`}>
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary text-foreground"><Icon className="h-4 w-4" /></span>
          <h3 className="font-['Space_Grotesk'] text-base font-semibold">{meta.label}</h3>
          <Badge variant="outline" className="rounded-full" data-testid={`ingredients-group-count-${testKey}`}>{total}</Badge>
        </div>
        {total === 0 ? (
          <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid={`ingredients-group-empty-${testKey}`}>
            {searching ? "Nothing matches this search." : `No ${meta.short} ingredients yet — add one with the button above (it will be filed under ${meta.short}).`}
          </p>
        ) : searching ? (
          // Searching → immediate FLAT results, no group to open first.
          <div className={dense ? "overflow-hidden rounded-xl border border-border bg-card" : "space-y-2"} data-testid={`ingredients-flat-${testKey}`}>{dense ? denseHeader : null}{groupsFor.flatMap(([, list]) => list).map(renderRow)}</div>
        ) : (
          groupsFor.map(([g, list]) => {
            const gk = `${key}:${g}`;
            const open = openGroups[gk] === true || groupsFor.length === 1;
            const lowCount = list.filter(ingredientIsLow).length;
            return (
              <div key={gk} className={`rounded-xl border border-border bg-card/60 ${dense ? "overflow-hidden" : "rounded-2xl"}`} data-testid={`ingredients-subgroup-${testKey}-${g.replace(/\s+/g, "-").toLowerCase()}`}>
                <button type="button" onClick={() => toggleGroup(gk)} className={`flex w-full items-center gap-2 text-left ${dense ? "min-h-10 px-2 py-1" : "min-h-14 gap-3 px-3 py-2"}`} aria-expanded={open} data-testid={`ingredients-subgroup-toggle-${testKey}-${g.replace(/\s+/g, "-").toLowerCase()}`}>
                  {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <span className="flex flex-1 items-baseline gap-2">
                    <span className={`font-semibold ${dense ? "text-sm" : "text-base"}`}>{g}</span>
                    <span className="text-xs text-muted-foreground">{list.length}{lowCount ? ` · ${lowCount} low` : ""}</span>
                  </span>
                  {lowCount ? <Badge className="rounded-full bg-destructive/10 text-destructive hover:bg-destructive/10">{lowCount} low</Badge> : null}
                </button>
                {open ? (dense ? <div className="border-t border-border">{denseHeader}{list.map(renderRow)}</div> : <div className="space-y-2 px-2 pb-2">{list.map(renderRow)}</div>) : null}
              </div>
            );
          })
        )}
      </section>
    );
  };

  const denseHeader = (
    <div className="hidden grid-cols-[minmax(0,1fr)_84px_84px_90px_auto] gap-2 border-b border-border bg-secondary/50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
      <span>Ingredient</span><span className="text-right">Floor</span><span className="text-right">Back</span><span className="text-right">Cost</span><span className="w-[72px]" />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="ingredients-panel">
      {/* Search first — results appear immediately, flat, no extra clicks. */}
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input type="search" placeholder="Search ingredients… (name, group)" value={filter.query} onChange={(e) => setFilter((f) => ({ ...f, query: e.target.value }))} className="h-10 rounded-xl pl-9" data-testid="ingredient-filter-query" />
          {filter.query ? <button type="button" onClick={() => setFilter((f) => ({ ...f, query: "" }))} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary" aria-label="Clear search" data-testid="ingredient-filter-clear"><X className="h-4 w-4" /></button> : null}
        </div>
        <div className="inline-flex flex-wrap rounded-xl border border-border bg-card p-1" data-testid="ingredient-filter-category">
          {[{ id: "all", label: `All · ${(ingredients || []).length}` }, ...sections.filter((s) => !s.key.startsWith("legacy:") || (counts.byKey[s.key] || 0) > 0 || !(departments || []).length).map((s) => ({ id: s.key, label: `${s.short} · ${counts.byKey[s.key] || 0}` }))].map((c) => (
            <button key={c.id} type="button" onClick={() => setFilter((f) => ({ ...f, category: c.id }))} className={`h-8 rounded-lg px-3 text-sm font-medium transition-colors ${filter.category === c.id ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`ingredient-filter-category-${c.id.startsWith("legacy:") ? c.id.slice(7) : c.id === "all" ? "all" : `dept-${c.id}`}`}>{c.label}</button>
          ))}
        </div>
        <Select value={filter.scope} onValueChange={(v) => setFilter((f) => ({ ...f, scope: v }))}>
          <SelectTrigger className="h-10 rounded-xl lg:w-44" data-testid="ingredient-filter-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stock levels</SelectItem>
            <SelectItem value="low">Low on the floor{counts.low ? ` (${counts.low})` : ""}</SelectItem>
            <SelectItem value="zero">Out of stock on the floor</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-2">
          {/* Session 63.C — "Move stock" now lives under Inventory → Stock Take. */}
          <Button className="h-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => {
            setCopyFrom(null);
            // Session 66 — "Add ingredient" lands in the department tab you are
            // looking at (Beach Bar tab → Beach Bar ingredient).
            const tabDept = filter.category !== "all" && !filter.category.startsWith("legacy:") && deptById.has(filter.category) ? filter.category : "";
            const tabCat = filter.category.startsWith("legacy:") ? filter.category.slice(7) : tabDept ? deptStationCategory(deptById.get(tabDept)) : null;
            setDraft((d) => ({ ...EMPTY_DRAFT, category: tabCat || d.category, department_id: tabDept || d.department_id || defaultDeptFor(tabCat || d.category) }));
            setAddOpen(true);
          }} data-testid="ingredient-add-open"><Plus className="h-4 w-4" /> Add ingredient</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5" data-testid="ingredients-density">
          <button type="button" onClick={() => !dense && toggleDense()} className={`h-7 rounded-md px-2 font-medium ${dense ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="ingredients-density-compact">Compact</button>
          <button type="button" onClick={() => dense && toggleDense()} className={`h-7 rounded-md px-2 font-medium ${!dense ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="ingredients-density-cards">Cards</button>
        </div>
        {!searching ? (
          <>
            <Button type="button" variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={() => expandAll(true)} data-testid="ingredients-expand-all">Open all groups</Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-xs" onClick={() => expandAll(false)} data-testid="ingredients-collapse-all">Collapse all</Button>
          </>
        ) : null}
        <span className="ml-auto text-muted-foreground">{searching ? `${visible.length} result${visible.length === 1 ? "" : "s"}` : `${(ingredients || []).length} ingredients`} · tap a name to edit · <b>± Stock</b> adjusts without opening</span>
      </div>
      {searching ? <span className="sr-only" data-testid="ingredients-search-summary">{visible.length} results</span> : <span className="sr-only" data-testid="ingredients-location-help">Tap a group to open it.</span>}

      {!loaded ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-2xl" />)}</div>
      ) : (ingredients || []).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="ingredients-empty">
          <Package className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-semibold">No ingredients yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Add your kitchen and bar ingredients, then link them to menu items under Recipes so sales deduct stock automatically.</p>
          <Button className="mt-4 h-11 rounded-xl" onClick={() => setAddOpen(true)} data-testid="ingredient-add-open-empty"><Plus className="h-4 w-4" /> Add your first ingredient</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((s) => renderCategory(s))}
        </div>
      )}

      {/* Add ingredient dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setDraft((d) => ({ ...EMPTY_DRAFT, category: d.category, department_id: d.department_id })); setMenuQuery(""); setCopyFrom(null); setCopyPickerOpen(false); setCopyQuery(""); } }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[24px] sm:max-w-lg" data-testid="ingredient-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{copyFrom ? "Copy ingredient" : "Add ingredient"}</DialogTitle>
            <DialogDescription>{copyFrom ? <>Duplicating <b>{copyFrom.name}</b> — pick the department the copy is used by and adjust anything that differs. Stock starts at what you enter below.</> : "Pick the department it is used by, then enter what is on the floor and what is kept back of house."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Session 63.C — Copy ingredient: duplicate an existing row (same unit,
                thresholds, cost, group, prep recipe, fallback policy) into another
                department instead of typing it all again. */}
            {copyFrom ? (
              <div className="flex items-center justify-between gap-2 rounded-2xl border border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/5 px-3 py-2" data-testid="ingredient-copy-source">
                <span className="flex min-w-0 items-center gap-2 text-sm"><Copy className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">Copying <b>{copyFrom.name}</b> <span className="text-muted-foreground">({deptLabel(copyFrom)})</span></span></span>
                <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => { setCopyFrom(null); setDraft((d) => ({ ...EMPTY_DRAFT, category: d.category, department_id: d.department_id })); }} data-testid="ingredient-copy-clear">Start blank</Button>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-3" data-testid="ingredient-copy-box">
                {!copyPickerOpen ? (
                  <Button type="button" variant="outline" className="h-10 w-full rounded-xl" onClick={() => setCopyPickerOpen(true)} data-testid="ingredient-copy-open"><Copy className="h-4 w-4" /> Copy an existing ingredient…</Button>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input autoFocus value={copyQuery} onChange={(e) => setCopyQuery(e.target.value)} placeholder="Search ingredients to copy…" className="h-10 rounded-xl bg-card pl-9" data-testid="ingredient-copy-search" />
                    </div>
                    <ul className="max-h-44 space-y-1 overflow-y-auto" data-testid="ingredient-copy-list">
                      {(ingredients || [])
                        .filter((i) => !copyQuery.trim() || (i.name || "").toLowerCase().includes(copyQuery.trim().toLowerCase()))
                        .slice(0, 40)
                        .map((i) => (
                          <li key={i.id}>
                            <button type="button" onClick={() => startCopy(i)} className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-card" data-testid={`ingredient-copy-option-${i.id}`}>
                              <span className="min-w-0 truncate font-medium">{i.name}</span>
                              <span className="shrink-0 text-xs text-muted-foreground">{deptLabel(i)} · {i.unit}</span>
                            </button>
                          </li>
                        ))}
                      {!(ingredients || []).length ? <li className="p-2 text-xs text-muted-foreground">No ingredients to copy yet.</li> : null}
                    </ul>
                    <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => { setCopyPickerOpen(false); setCopyQuery(""); }} data-testid="ingredient-copy-cancel">Cancel</Button>
                  </div>
                )}
              </div>
            )}
            {!copyFrom ? (
            <div>
              <Label className="text-xs">What is it?</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Kind">
                {[
                  { id: "raw", label: "Raw ingredient", hint: "used in recipes (patties, gin…)" },
                  { id: "item", label: "Item sold as-is", hint: "cans, bottles, retail — one menu item" },
                ].map((k) => {
                  const active = draft.kind === k.id;
                  return (
                    <button key={k.id} type="button" role="radio" aria-checked={active} onClick={() => { setDraft((d) => ({ ...d, kind: k.id })); if (k.id === "item") loadMenuItems(); }} className={`min-h-14 rounded-xl border px-3 text-left transition-colors ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-border bg-card hover:bg-secondary"}`} data-testid={`ingredient-new-kind-${k.id}`}>
                      <span className="block text-sm font-semibold">{k.label}</span>
                      <span className="block text-[11px] text-muted-foreground">{k.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            ) : null}
            {draft.kind === "item" ? (
              <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="ingredient-new-item-picker">
                <Label className="text-xs">Which menu item is it sold as?</Label>
                {draft.linked_menu_item_id ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl border border-[hsl(var(--primary))]/50 bg-card px-3 py-2" data-testid="ingredient-new-linked-item">
                    <span className="truncate text-sm font-medium">{draft.linked_menu_item_name}</span>
                    <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => setDraft((d) => ({ ...d, linked_menu_item_id: "", linked_menu_item_name: "" }))} data-testid="ingredient-new-linked-clear">Change</Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input value={menuQuery} onChange={(e) => setMenuQuery(e.target.value)} placeholder="Search your menu…" className="h-11 rounded-xl bg-card pl-9" data-testid="ingredient-new-item-search" />
                    </div>
                    <ul className="max-h-48 space-y-1 overflow-y-auto" data-testid="ingredient-new-item-list">
                      {menuItems === null ? (
                        <li className="p-2 text-xs text-muted-foreground">Loading menu…</li>
                      ) : (menuItems || [])
                        .filter((mi) => !menuQuery.trim() || (mi.name || "").toLowerCase().includes(menuQuery.trim().toLowerCase()))
                        .slice(0, 60)
                        .map((mi) => {
                          const taken = linkedItemIds.has(mi.id);
                          return (
                            <li key={mi.id}>
                              <button type="button" disabled={taken} onClick={() => pickMenuItem(mi)} className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-card disabled:cursor-not-allowed disabled:opacity-50" data-testid={`ingredient-new-item-option-${mi.id}`}>
                                <span className="min-w-0 truncate"><span className="font-medium">{mi.name}</span>{mi.department ? <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">{mi.department}</span> : null}</span>
                                <span className="shrink-0 text-xs text-muted-foreground">{taken ? "already tracked" : (mi.stock !== null && mi.stock !== undefined ? `${mi.stock} in stock` : "")}</span>
                              </button>
                            </li>
                          );
                        })}
                      {menuItems && menuItems.length === 0 ? <li className="p-2 text-xs text-muted-foreground">No menu items yet.</li> : null}
                    </ul>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Deduct per sale</Label>
                  <Input type="number" min="0.001" step="1" inputMode="decimal" value={draft.link_quantity} onChange={(e) => setDraft((d) => ({ ...d, link_quantity: e.target.value }))} className="h-10 w-24 rounded-xl bg-card text-right tabular-nums" data-testid="ingredient-new-link-qty" />
                  <span className="text-xs text-muted-foreground">{draft.unit} each time it is ordered</span>
                </div>
                <p className="text-[11px] text-muted-foreground">No recipe needed — we link it for you. Stock comes off the floor count automatically and goes back if the order is voided.</p>
              </div>
            ) : null}
            <div>
              <Label className="text-xs">Used by <span className="text-muted-foreground">(exactly one department)</span></Label>
              {(departments || []).length ? (
                <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Department" data-testid="ingredient-new-departments">
                  {departments.map((d) => {
                    const Icon = deptStationCategory(d) === "bar" ? Wine : ChefHat;
                    const active = draft.department_id === d.id;
                    return (
                      <button key={d.id} type="button" role="radio" aria-checked={active} onClick={() => setDraft((x) => ({ ...x, department_id: d.id, category: deptStationCategory(d) }))} className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold transition-colors ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"}`} data-testid={`ingredient-new-department-${d.id}`}>
                        <Icon className="h-4 w-4" /> <span className="truncate">{d.name}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Category">
                  {Object.entries(CATEGORY_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    const active = draft.category === key;
                    return (
                      <button key={key} type="button" role="radio" aria-checked={active} onClick={() => setDraft((d) => ({ ...d, category: key }))} className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold transition-colors ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"}`} data-testid={`ingredient-new-category-${key}`}>
                        <Icon className="h-4 w-4" /> {meta.short}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Name {draft.kind === "item" ? <span className="text-muted-foreground">(pre-filled from the menu item — edit if you like)</span> : null}</Label>
              <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder={draft.kind === "item" ? "e.g. Cola 330ml can" : (draft.category === "bar" ? "e.g. Lime wedges" : "e.g. Beef patties")} className="mt-1 h-11 rounded-xl" autoFocus={draft.kind === "raw"} data-testid="ingredient-new-name" />
            </div>
            <div>
              <Label className="text-xs">Group <span className="text-muted-foreground">(optional — keeps the list tidy)</span></Label>
              <Input list="ing-new-groups" value={draft.group || ""} onChange={(e) => setDraft((d) => ({ ...d, group: e.target.value }))} placeholder="e.g. Dairy, Meat, Produce, Sauces" className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-group" />
              <datalist id="ing-new-groups">{groupSuggestions.map((g) => <option key={g} value={g} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unit</Label>
                <Select value={draft.unit} onValueChange={(v) => setDraft((d) => ({ ...d, unit: v }))}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Low alert ≤ ({draft.unit})</Label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={draft.low_stock_threshold} onChange={(e) => setDraft((d) => ({ ...d, low_stock_threshold: e.target.value }))} placeholder="10" className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-threshold" />
              </div>
              <div>
                <Label className="text-xs">On the floor ({draft.unit})</Label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={draft.stock_on_hand} onChange={(e) => setDraft((d) => ({ ...d, stock_on_hand: e.target.value }))} placeholder="0" className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-stock" />
              </div>
              <div>
                <Label className="text-xs">Back of house ({draft.unit})</Label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={draft.back_of_house_stock} onChange={(e) => setDraft((d) => ({ ...d, back_of_house_stock: e.target.value }))} placeholder="0" className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-boh" />
              </div>
              <div>
                <Label className="text-xs">Cost per {draft.unit} (R)</Label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={draft.cost_per_unit} onChange={(e) => setDraft((d) => ({ ...d, cost_per_unit: e.target.value }))} placeholder="what you pay" className="mt-1 h-11 rounded-xl" data-testid="ingredient-new-cost" />
              </div>
              {draft.unit === "pcs" ? (
                <div>
                  <Label className="text-xs">Each piece equals</Label>
                  <div className="mt-1 flex gap-2">
                    <Input type="number" min="0" step="0.1" inputMode="decimal" value={draft.weight_per_unit_g} onChange={(e) => setDraft((d) => ({ ...d, weight_per_unit_g: e.target.value }))} placeholder="optional" className="h-11 rounded-xl" data-testid="ingredient-new-weight" />
                    <Select value={draft.piece_unit} onValueChange={(v) => setDraft((d) => ({ ...d, piece_unit: v }))}>
                      <SelectTrigger className="h-11 w-20 shrink-0 rounded-xl" data-testid="ingredient-new-piece-unit"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="g">g</SelectItem><SelectItem value="ml">ml</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              ) : null}
            </div>
            {draft.unit === "pcs" ? (
              <p className="text-xs text-muted-foreground"><strong>Each piece equals</strong> is optional — e.g. "1 cheese slice = 20 g" lets recipes written in grams convert to pieces automatically.</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setAddOpen(false)} data-testid="ingredient-add-cancel">Cancel</Button>
            <Button className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={create} disabled={adding} data-testid="ingredient-add-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : (copyFrom ? <Copy className="h-4 w-4" /> : <Plus className="h-4 w-4" />)} {copyFrom ? "Create copy" : "Add ingredient"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MoveStockDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        direction={moveDirection}
        ingredients={ingredients}
        http={http}
        authHeaders={authHeaders}
        recorder={recorder}
        onDone={async () => { setMoveOpen(false); await onRefresh?.(); }}
        sessionForEvents={sessionForEvents}
      />
    </div>
  );
};

/**
 * "Move stock to floor" / "Send back to storeroom" — tick the ingredients,
 * type how many of each, confirm. Quantities are clamped to what is
 * available at the source location.
 */
export const MoveStockDialog = ({ open, onOpenChange, direction, ingredients, http, authHeaders, recorder, onDone, sessionForEvents = null }) => {
  const toFloor = direction === "to_floor";
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [picked, setPicked] = useState({}); // id -> { checked, qty }
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) { setPicked({}); setQuery(""); setCategory("all"); setNotes(""); }
  }, [open, direction]);

  const source = toFloor ? "back_of_house_stock" : "stock_on_hand";
  // Session 66 — stock is moved ONE department at a time. A transfer only
  // ever moves an ingredient between two locations, so it can never cross
  // departments (Beach Bar vodka stays Beach Bar vodka back of house or on
  // the floor) — the tabs just keep the list to the department you are in.
  const deptKey = (ing) => (ing.department_id ? `dept:${ing.department_id}` : `legacy:${(ing.category || "kitchen") === "bar" ? "bar" : "kitchen"}`);
  const deptTabs = useMemo(() => {
    const seen = new Map();
    (ingredients || []).filter((ing) => num(ing[source]) > 0).forEach((ing) => {
      const key = deptKey(ing);
      if (!seen.has(key)) seen.set(key, { id: key, label: ing.department_id ? (ing.department_name || "Department") : (key === "legacy:bar" ? "Bar" : "Kitchen"), dept: !!ing.department_id, count: 0 });
      seen.get(key).count += 1;
    });
    const list = [...seen.values()].sort((a, b) => (a.dept === b.dept ? a.label.localeCompare(b.label) : a.dept ? -1 : 1));
    if (list.some((t) => t.dept)) list.forEach((t) => { if (!t.dept) t.label = `${t.label} (unassigned)`; });
    return list;
  }, [ingredients, source]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (ingredients || [])
      .filter((ing) => num(ing[source]) > 0)
      .filter((ing) => !q || (ing.name || "").toLowerCase().includes(q))
      .filter((ing) => category === "all" || deptKey(ing) === category)
      .sort((a, b) => {
        const aLow = ingredientIsLow(a) ? 0 : 1;
        const bLow = ingredientIsLow(b) ? 0 : 1;
        return aLow - bLow || (a.name || "").localeCompare(b.name || "");
      });
  }, [ingredients, query, category, source]);

  const selected = Object.entries(picked).filter(([, v]) => v.checked && num(v.qty) > 0);
  const suggestFor = (ing) => {
    // Top the floor back up to twice the low-alert level (a sensible
    // "restock" amount), capped by what the storeroom holds.
    const available = num(ing[source]);
    if (!toFloor) return available;
    const target = Math.max(num(ing.low_stock_threshold, 10) * 2 - num(ing.stock_on_hand), 0);
    const suggestion = target > 0 ? Math.min(target, available) : Math.min(available, num(ing.low_stock_threshold, 10) || available);
    return Math.round(suggestion * 100) / 100;
  };
  const toggle = (ing, checked) => setPicked((p) => ({ ...p, [ing.id]: { checked, qty: p[ing.id]?.qty ?? String(suggestFor(ing)) } }));
  const setQty = (ing, qty) => setPicked((p) => ({ ...p, [ing.id]: { checked: true, qty } }));
  const selectLow = () => {
    const next = { ...picked };
    rows.filter((ing) => ingredientIsLow(ing)).forEach((ing) => { next[ing.id] = { checked: true, qty: next[ing.id]?.qty ?? String(suggestFor(ing)) }; });
    setPicked(next);
  };
  const selectAll = (checked) => {
    const next = { ...picked };
    rows.forEach((ing) => { next[ing.id] = { checked, qty: next[ing.id]?.qty ?? String(suggestFor(ing)) }; });
    setPicked(next);
  };

  const submit = async () => {
    const byId = new Map((ingredients || []).map((i) => [i.id, i]));
    const lines = selected.map(([id, v]) => {
      const ing = byId.get(id);
      const available = ing ? num(ing[source]) : 0;
      return { ingredient_id: id, quantity: Math.min(num(v.qty), available) };
    }).filter((l) => l.quantity > 0);
    if (!lines.length) { toast.error("Tick at least one ingredient and enter a quantity."); return; }
    setSaving(true);
    try {
      // Session 40 — a PHYSICAL transfer, offline-safe: same body over HTTP
      // with a change_id, or queued as `stock_transferred` (location keys are
      // accepted by the server) when the connection is down.
      const res = await performStockAction({
        http, authHeaders, path: "/owner/inventory/transfer",
        body: { from_location_id: toFloor ? "back_of_house" : "floor", to_location_id: toFloor ? "floor" : "back_of_house", lines, user: recorder || null, notes: notes.trim() || null },
        eventType: EventType.STOCK_TRANSFERRED, session: sessionForEvents || null,
      });
      if (res.status === "queued") {
        toast.info(`${lines.length} transfer line${lines.length === 1 ? "" : "s"} saved on this device — will sync when the connection returns.`);
        await onDone?.(null);
        return;
      }
      const moved = res.data?.moved || [];
      toast.success(`${moved.length} ingredient${moved.length === 1 ? "" : "s"} moved ${toFloor ? "to the floor" : "back of house"}.`);
      await onDone?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not move stock.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col rounded-[24px] p-0 sm:max-w-2xl" data-testid="move-stock-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl">
            {toFloor ? <ArrowRightLeft className="h-5 w-5" /> : <Warehouse className="h-5 w-5" />}
            {toFloor ? "Move stock to the floor" : "Send stock back of house"}
          </DialogTitle>
          <DialogDescription>
            {toFloor
              ? "Tick what is going out for service and how much. It comes off back of house and is added to the floor count."
              : "Tick what is going back into the freezers or storeroom. It comes off the floor count and is added back of house."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 px-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="h-11 rounded-xl pl-9" data-testid="move-stock-search" />
            </div>
            <div className="inline-flex flex-wrap rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Department" data-testid="move-stock-dept-tabs">
              {[{ id: "all", label: "All departments" }, ...deptTabs].map((c) => (
                <button key={c.id} type="button" role="tab" aria-selected={category === c.id} onClick={() => setCategory(c.id)} className={`h-9 rounded-lg px-3 text-xs font-medium transition-colors ${category === c.id ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`move-stock-category-${c.id.replace(/^dept:/, "dept-").replace(/^legacy:/, "")}`}>{c.label}{c.count !== undefined ? <span className="ml-1 rounded-full bg-background/60 px-1.5 text-[10px] font-semibold">{c.count}</span> : null}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Button type="button" size="sm" variant="outline" className="h-9 rounded-xl" onClick={() => selectAll(true)} data-testid="move-stock-select-all">Select all</Button>
            {toFloor ? <Button type="button" size="sm" variant="outline" className="h-9 rounded-xl" onClick={selectLow} data-testid="move-stock-select-low">Select low on floor</Button> : null}
            <Button type="button" size="sm" variant="ghost" className="h-9 rounded-xl" onClick={() => selectAll(false)} data-testid="move-stock-clear">Clear</Button>
            <span className="ml-auto text-muted-foreground" data-testid="move-stock-selected-count">{selected.length} selected</span>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">
          {rows.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="move-stock-empty">
              {toFloor ? "Nothing is stocked back of house yet. Enter back-of-house counts on the ingredient cards first." : "Nothing on the floor matches."}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="move-stock-rows">
              {rows.map((ing) => {
                const p = picked[ing.id] || { checked: false, qty: "" };
                const available = num(ing[source]);
                const low = ingredientIsLow(ing);
                const qtyNum = num(p.qty);
                const over = qtyNum > available;
                return (
                  <li key={ing.id} className={`rounded-2xl border p-3 transition-colors ${p.checked ? "border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/5" : "border-border"}`} data-testid={`move-stock-row-${ing.id}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox checked={!!p.checked} onCheckedChange={(c) => toggle(ing, !!c)} className="mt-1 h-6 w-6 rounded-md" aria-label={`Select ${ing.name}`} data-testid={`move-stock-check-${ing.id}`} />
                      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => toggle(ing, !p.checked)}>
                        <p className="flex flex-wrap items-center gap-2 font-medium">
                          <span className="truncate">{ing.name}</span>
                          <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`move-stock-dept-${ing.id}`}>{ing.department_name || (ing.category === "bar" ? "Bar" : "Kitchen")}</Badge>
                          {low ? <Badge className="rounded-full bg-destructive/10 text-[10px] text-destructive hover:bg-destructive/10">Low on floor</Badge> : null}
                        </p>
                        <p className="text-xs text-muted-foreground tabular-nums">
                          Floor {fmtQty(ing.stock_on_hand)} {ing.unit} · Back of house {fmtQty(ing.back_of_house_stock)} {ing.unit} · low alert ≤ {fmtQty(ing.low_stock_threshold)}
                        </p>
                      </button>
                      <div className="w-32 shrink-0">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" min="0" step="0.01" inputMode="decimal"
                            value={p.qty}
                            onChange={(e) => setQty(ing, e.target.value)}
                            placeholder="0"
                            className={`h-11 rounded-xl text-right tabular-nums ${over ? "border-destructive" : ""}`}
                            aria-label={`Quantity of ${ing.name} to move`}
                            data-testid={`move-stock-qty-${ing.id}`}
                          />
                          <span className="w-8 text-xs text-muted-foreground">{ing.unit}</span>
                        </div>
                        <button type="button" className="mt-1 w-full text-right text-[11px] text-muted-foreground underline-offset-2 hover:underline" onClick={() => setQty(ing, String(available))} data-testid={`move-stock-max-${ing.id}`}>
                          max {fmtQty(available)}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="space-y-3 border-t border-border px-5 py-4">
          <div>
            <Label className="text-xs">Note <span className="text-muted-foreground">(optional — shows in stock history)</span></Label>
            <Textarea rows={1} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 min-h-11 rounded-xl" placeholder={toFloor ? "e.g. Friday night prep" : "e.g. unopened, back to freezer"} data-testid="move-stock-notes" />
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <p className="hidden text-xs text-muted-foreground sm:block">Recorded by <span className="font-medium text-foreground">{recorder || "current user"}</span></p>
            <div className="flex gap-2">
              <Button variant="outline" className="h-11 rounded-xl" onClick={() => onOpenChange(false)} data-testid="move-stock-cancel">Cancel</Button>
              <Button className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={saving || !selected.length} onClick={submit} data-testid="move-stock-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
                {toFloor ? `Move ${selected.length || ""} to floor` : `Send ${selected.length || ""} back of house`}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
